import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions, MODULES, ACTIONS } from '../context/PermissionContext';
import api from '../services/api';
import { formatDateOnly } from '../utils/dateOnly';
import './Settings.css';

// Module display label: metadata from PermissionContext (the frontend's
// label/icon source of truth); module/action STRUCTURE comes from the backend
// catalog via usePermissions().moduleCatalog. Prettified-key fallback so a
// catalog module with no MODULES entry never renders blank.
const moduleLabel = (module) =>
  MODULES[module]?.label || module.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const Settings = () => {
  const { user, sessionId } = useAuth();
  const { hasPermission, moduleCatalog } = usePermissions();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Profile state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // System settings state
  const [systemSettings, setSystemSettings] = useState({});
  const [settingChanges, setSettingChanges] = useState({});
  // Login throttle (P1-C backend; throttle-UI slice): every row, locked or lapsed
  const [throttle, setThrottle] = useState([]);
  // NR1 notification routing card (docs/NOTIF_ROUTING_PROGRAM.md §3.8): the
  // 'notifications'/'email_routing' json document, edited as an object and
  // saved through its OWN explicit Save (the never-saved finding: nothing
  // here rides the shared settingChanges buffer). routingSaved is the
  // canonical string as last loaded, so "unsaved changes" is a string compare.
  const [routing, setRouting] = useState(null);
  const [routingSaved, setRoutingSaved] = useState('');
  const [routingRoles, setRoutingRoles] = useState([]);
  const [routingDepartments, setRoutingDepartments] = useState([]);
  const [routingErrors, setRoutingErrors] = useState([]);
  const [routingDeptDraft, setRoutingDeptDraft] = useState({});
  
  // Hero image upload state
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingSupportHero, setUploadingSupportHero] = useState(false);
  const heroFileInputRef = useRef(null);
  const supportHeroFileInputRef = useRef(null);
  
  // Roles state
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  
  // User overrides state
  const [userOverrides, setUserOverrides] = useState([]);
  const [users, setUsers] = useState([]);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [newOverride, setNewOverride] = useState({
    user_id: '', module: '', action: '', granted: true, reason: ''
  });
  
  // Audit log state
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilters, setAuditFilters] = useState({
    user_id: '', action: '', entity_type: '', start_date: '', end_date: ''
  });
  const [auditPagination, setAuditPagination] = useState({ total: 0, limit: 50, offset: 0 });
  
  // Sessions state
  const [sessions, setSessions] = useState([]);
  
  // API info state
  const [apiHealth, setApiHealth] = useState(null);

  // Departments state (TO1a / R-TO4)
  const [departments, setDepartments] = useState([]);
  const [deptDraft, setDeptDraft] = useState({ name: '', color: '#3B82F6', manager_user_id: '' });
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editingDept, setEditingDept] = useState({ name: '', color: '', manager_user_id: '' });

  // Blackout dates (TO1c / R-TO7, ruling 11). Same tab, same gate as
  // departments: time_off:manage is the GM's configuration surface. Blackouts
  // WARN; nothing created here refuses anybody's request.
  const [blackouts, setBlackouts] = useState([]);
  const [blackoutDraft, setBlackoutDraft] = useState({ start_date: '', end_date: '', reason: '' });

  const canManage = hasPermission('settings', 'manage');
  // The Departments card gates on time_off:manage, NOT settings:manage — the GM
  // owns departments (ruling 11 gives them manage) without necessarily owning
  // the rest of Settings.
  const canManageDepartments = hasPermission('time_off', 'manage');

  const createDepartment = async () => {
    if (!deptDraft.name.trim()) { showMessage('error', 'Department name is required'); return; }
    try {
      setLoading(true);
      await api.post('/departments', {
        name: deptDraft.name.trim(),
        color: deptDraft.color,
        manager_user_id: deptDraft.manager_user_id || null
      });
      setDeptDraft({ name: '', color: '#3B82F6', manager_user_id: '' });
      await fetchDepartments();
      showMessage('success', 'Department created');
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to create department');
    } finally { setLoading(false); }
  };

  const saveDepartment = async (id) => {
    try {
      setLoading(true);
      await api.put(`/departments/${id}`, {
        name: editingDept.name.trim(),
        color: editingDept.color,
        manager_user_id: editingDept.manager_user_id || null
      });
      setEditingDeptId(null);
      await fetchDepartments();
      showMessage('success', 'Department saved');
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to save department');
    } finally { setLoading(false); }
  };

  const toggleDepartmentActive = async (dept) => {
    try {
      setLoading(true);
      await api.put(`/departments/${dept.id}`, { is_active: !dept.is_active });
      await fetchDepartments();
      showMessage('success', dept.is_active ? 'Department deactivated' : 'Department activated');
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to update department');
    } finally { setLoading(false); }
  };

  const fetchBlackouts = useCallback(async () => {
    try {
      const res = await api.get('/time-off/blackouts?all=true');
      setBlackouts(res.data.blackouts || []);
    } catch (err) {
      console.error('Failed to load blackouts:', err);
    }
  }, []);

  const createBlackout = async () => {
    try {
      setLoading(true);
      await api.post('/time-off/blackouts', {
        start_date: blackoutDraft.start_date,
        end_date: blackoutDraft.end_date,
        reason: blackoutDraft.reason
      });
      setBlackoutDraft({ start_date: '', end_date: '', reason: '' });
      await fetchBlackouts();
      showMessage('success', 'Blackout added');
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to add blackout');
    } finally { setLoading(false); }
  };

  // There is no hard delete (R-TO7): a blackout that has already warned
  // somebody is deactivated, not erased.
  const toggleBlackoutActive = async (b) => {
    try {
      setLoading(true);
      await api.put(`/time-off/blackouts/${b.id}`, { is_active: !b.is_active });
      await fetchBlackouts();
      showMessage('success', b.is_active ? 'Blackout deactivated' : 'Blackout reactivated');
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to update blackout');
    } finally { setLoading(false); }
  };

  // Show message helper (renders as a fixed toast — see the message JSX)
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4500);
  };

  // Fetch system settings
  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/settings');
      setSystemSettings(res.data.grouped || {});
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  }, []);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const res = await api.get('/settings/roles');
      setRoles(res.data || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  }, []);

  // Fetch role permissions — defaults-aware endpoint: returns DB rows if the
  // role has any, else the code defaults (DEFAULT_ROLE_PERMISSIONS). Seeding
  // the matrix with the EFFECTIVE set is what makes the full-replace save
  // non-destructive (an unchanged save re-writes the same effective set).
  const fetchRolePermissions = useCallback(async (roleName) => {
    try {
      const res = await api.get(`/permissions/roles/${roleName}`);
      setRolePermissions(res.data.permissions || {});
    } catch (err) {
      console.error('Failed to load role permissions:', err);
    }
  }, []);

  // Fetch user overrides
  const fetchUserOverrides = useCallback(async () => {
    try {
      const res = await api.get('/settings/user-overrides');
      setUserOverrides(res.data || []);
    } catch (err) {
      console.error('Failed to load user overrides:', err);
    }
  }, []);

  // Fetch users for override modal
  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data.users || res.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    try {
      const res = await api.get('/departments?all=true');
      setDepartments(res.data.departments || []);
    } catch (err) {
      console.error('Failed to load departments:', err);
    }
  }, []);

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        limit: auditPagination.limit,
        offset: auditPagination.offset,
        ...Object.fromEntries(Object.entries(auditFilters).filter(([_, v]) => v))
      });
      const res = await api.get(`/settings/audit?${params}`);
      setAuditLogs(res.data.logs || []);
      setAuditPagination(prev => ({ ...prev, total: res.data.total }));
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    }
  }, [auditFilters, auditPagination.limit, auditPagination.offset]);

  // Fetch active sessions
  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.get('/settings/sessions');
      setSessions(res.data || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  }, []);

  // Fetch login-throttle records (locked AND lapsed — a lapsed row still
  // counts inside the 15-minute window).
  const fetchThrottle = useCallback(async () => {
    try {
      const res = await api.get('/settings/login-throttle');
      setThrottle(Array.isArray(res.data?.records) ? res.data.records : []);
    } catch (err) {
      console.error('Failed to load login throttle:', err);
    }
  }, []);

  // NR1: load the routing document + the pick-lists (roles table, distinct
  // departments in use). A missing or unparseable row renders the card empty
  // with a note rather than a blank card.
  const fetchRouting = useCallback(async () => {
    try {
      const [cat, rolesRes, deptRes] = await Promise.all([
        api.get('/settings/category/notifications'),
        api.get('/settings/roles'),
        api.get('/settings/departments')
      ]);
      const row = (cat.data || []).find(s => s.setting_key === 'email_routing');
      let doc = null;
      try { doc = row ? JSON.parse(row.setting_value) : null; } catch (e) { doc = null; }
      setRouting(doc);
      setRoutingSaved(doc ? JSON.stringify(doc) : '');
      setRoutingRoles(Array.isArray(rolesRes.data) ? rolesRes.data : []);
      setRoutingDepartments(Array.isArray(deptRes.data) ? deptRes.data : []);
      setRoutingErrors([]);
    } catch (err) {
      console.error('Failed to load notification routing:', err);
    }
  }, []);

  const routingDirty = routing !== null && JSON.stringify(routing) !== routingSaved;

  const updateRoute = (type, patch) => {
    setRouting(prev => ({ ...prev, [type]: { ...prev[type], ...patch } }));
  };

  const toggleRouteListItem = (type, field, item) => {
    setRouting(prev => {
      const list = prev[type][field] || [];
      const next = list.includes(item) ? list.filter(x => x !== item) : [...list, item];
      return { ...prev, [type]: { ...prev[type], [field]: next } };
    });
  };

  const addRouteDepartment = (type) => {
    const raw = (routingDeptDraft[type] || '').trim();
    if (!raw) return;
    setRouting(prev => {
      const list = prev[type].departments || [];
      const exists = list.some(d => d.toLowerCase() === raw.toLowerCase());
      return exists ? prev : { ...prev, [type]: { ...prev[type], departments: [...list, raw] } };
    });
    setRoutingDeptDraft(prev => ({ ...prev, [type]: '' }));
  };

  // Save through the real single-key settings PUT; the server validates and
  // returns 400 + reasons on a bad document. On success the card RELOADS
  // from the server so what you see is what was stored.
  const saveRouting = async () => {
    try {
      setLoading(true);
      setRoutingErrors([]);
      await api.put('/settings/notifications/email_routing', { value: JSON.stringify(routing) });
      await fetchRouting();
      showMessage('success', 'Notification routing saved');
    } catch (err) {
      const errors = err.response?.data?.errors;
      setRoutingErrors(Array.isArray(errors) ? errors : []);
      showMessage('error', err.response?.data?.message || 'Failed to save notification routing');
    } finally {
      setLoading(false);
    }
  };

  // Fetch API health
  const fetchApiHealth = useCallback(async () => {
    try {
      const res = await api.get('/health');
      setApiHealth(res.data);
    } catch (err) {
      console.error('Failed to get API health:', err);
    }
  }, []);

  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'appearance' && canManage) fetchSettings();
    if (activeTab === 'system' && canManage) { fetchSettings(); fetchThrottle(); fetchRouting(); }
    if (activeTab === 'roles' && canManage) fetchRoles();
    if (activeTab === 'overrides' && canManage) {
      fetchUserOverrides();
      fetchUsers();
    }
    if (activeTab === 'audit' && canManage) fetchAuditLogs();
    if (activeTab === 'sessions' && canManage) fetchSessions();
    if (activeTab === 'profile') fetchApiHealth();
    if (activeTab === 'departments' && canManageDepartments) { fetchDepartments(); fetchUsers(); fetchBlackouts(); }
  }, [activeTab, canManage, canManageDepartments, fetchSettings, fetchThrottle, fetchRouting, fetchRoles, fetchUserOverrides, fetchUsers, fetchAuditLogs, fetchSessions, fetchApiHealth, fetchDepartments, fetchBlackouts]);

  // Load role permissions when role selected
  useEffect(() => {
    if (selectedRole) {
      fetchRolePermissions(selectedRole);
    }
  }, [selectedRole, fetchRolePermissions]);

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }

    // P2-A (R3/R7): client mirror of the server policy — 12–72; the server also breach-checks.
    if (passwordForm.newPassword.length < 12 || passwordForm.newPassword.length > 72) {
      showMessage('error', 'Password must be between 12 and 72 characters');
      return;
    }

    try {
      setLoading(true);
      const res = await api.post('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      // R7: every OTHER session was signed out; name the count when there were any.
      const revoked = Number(res.data?.sessions_revoked) || 0;
      showMessage('success', revoked > 0
        ? `Password changed — ${revoked} other session${revoked === 1 ? '' : 's'} signed out.`
        : 'Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      // axios client: {message} bodies (policy/current-password) carry the text;
      // a validator {errors} body has none → first msg, else the generic line.
      showMessage('error', err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Handle system setting change
  const handleSettingChange = (category, key, value) => {
    setSettingChanges(prev => ({
      ...prev,
      [`${category}.${key}`]: { category, key, value }
    }));
  };

  // Save system settings
  const saveSystemSettings = async () => {
    try {
      setLoading(true);
      const settings = Object.values(settingChanges);
      await api.put('/settings', { settings });
      showMessage('success', 'Settings saved!');
      setSettingChanges({});
      fetchSettings();
    } catch (err) {
      showMessage('error', 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  // Handle hero image upload
  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Please select an image file (JPEG, PNG, etc.)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showMessage('error', 'Image must be smaller than 10MB');
      return;
    }

    try {
      setUploadingHero(true);
      
      // Upload to S3 via CMS endpoint
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'website/dashboard');
      
      const uploadRes = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = uploadRes.data.url;
      
      // Save the URL to settings
      await api.put('/settings', {
        settings: [{ category: 'appearance', key: 'hero_image_url', value: imageUrl }]
      });
      
      showMessage('success', 'Hero image uploaded successfully!');
      fetchSettings();
      
    } catch (err) {
      console.error('Upload error:', err);
      showMessage('error', err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingHero(false);
      // Reset file input
      if (heroFileInputRef.current) {
        heroFileInputRef.current.value = '';
      }
    }
  };

  // Handle support page hero image upload
  const handleSupportHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showMessage('error', 'Please select an image file (JPEG, PNG, etc.)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showMessage('error', 'Image must be smaller than 10MB');
      return;
    }

    try {
      setUploadingSupportHero(true);

      // Upload to S3 via CMS endpoint
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', 'website/support');

      const uploadRes = await api.post('/cms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = uploadRes.data.url;

      // Save the URL to settings
      await api.put('/settings', {
        settings: [{ category: 'appearance', key: 'support_hero_image_url', value: imageUrl }]
      });

      showMessage('success', 'Support page hero image uploaded successfully!');
      fetchSettings();

    } catch (err) {
      console.error('Upload error:', err);
      showMessage('error', err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingSupportHero(false);
      // Reset file input
      if (supportHeroFileInputRef.current) {
        supportHeroFileInputRef.current.value = '';
      }
    }
  };

  // Toggle role permission
  const toggleRolePermission = (module, action) => {
    setRolePermissions(prev => {
      const modulePerms = prev[module] || [];
      if (modulePerms.includes(action)) {
        return { ...prev, [module]: modulePerms.filter(a => a !== action) };
      } else {
        return { ...prev, [module]: [...modulePerms, action] };
      }
    });
  };

  // Save role permissions (backend does a full replace — confirm with an
  // exact count so the admin sees what the role ends up with)
  const saveRolePermissions = async () => {
    const grantedModules = Object.entries(rolePermissions).filter(([, actions]) => actions.length > 0);
    const totalPermissions = grantedModules.reduce((sum, [, actions]) => sum + actions.length, 0);
    if (!window.confirm(
      `Save permissions for ${selectedRole}? This will set ${selectedRole} to exactly ` +
      `${totalPermissions} permissions across ${grantedModules.length} modules, replacing all ` +
      `existing permissions for this role. Continue?`
    )) return;
    try {
      setLoading(true);
      await api.put(`/settings/roles/${selectedRole}/permissions`, { permissions: rolePermissions });
      showMessage('success', `Permissions saved for ${selectedRole}!`);
      fetchRoles();
    } catch (err) {
      showMessage('error', 'Failed to save permissions');
    } finally {
      setLoading(false);
    }
  };

  // Add user override
  const addUserOverride = async () => {
    try {
      setLoading(true);
      await api.post('/settings/user-overrides', newOverride);
      showMessage('success', 'Permission override added!');
      setShowOverrideModal(false);
      setNewOverride({ user_id: '', module: '', action: '', granted: true, reason: '' });
      fetchUserOverrides();
    } catch (err) {
      showMessage('error', 'Failed to add override');
    } finally {
      setLoading(false);
    }
  };

  // Delete user override
  const deleteOverride = async (id) => {
    if (!window.confirm('Remove this permission override?')) return;
    try {
      await api.delete(`/settings/user-overrides/${id}`);
      showMessage('success', 'Override removed');
      fetchUserOverrides();
    } catch (err) {
      showMessage('error', 'Failed to remove override');
    }
  };

  // Terminate one session (a refresh-token family): refresh dead now, its
  // sockets closed now, its access token refused on the next request.
  const terminateSession = async (familyId) => {
    if (!window.confirm('Terminate this session? The user will be signed out on that device.')) return;
    try {
      await api.delete(`/settings/sessions/${familyId}`);
      showMessage('success', 'Session terminated');
      fetchSessions();
    } catch (err) {
      showMessage('error', 'Failed to terminate session');
    }
  };

  // Sign a user out of every device (all their sessions).
  const signOutEverywhere = async (userId, userName) => {
    if (!window.confirm(`Sign ${userName} out of every device? All of their sessions will be revoked.`)) return;
    try {
      await api.delete(`/settings/sessions/user/${userId}`);
      showMessage('success', `${userName} signed out everywhere`);
      fetchSessions();
    } catch (err) {
      showMessage('error', 'Failed to revoke sessions');
    }
  };

  // Clear a login-throttle row (the P1-C admin clear; audited THROTTLE_CLEARED
  // server-side). The key is a raw identifier (email / digits / user:<id>) —
  // encoded for the path segment.
  const unlockIdentifier = async (identifier) => {
    if (!window.confirm(`Unlock ${identifier}? Its failed-attempt counter will be cleared.`)) return;
    try {
      await api.delete(`/settings/login-throttle/${encodeURIComponent(identifier)}`);
      showMessage('success', `Unlocked ${identifier}`);
      fetchThrottle();
    } catch (err) {
      showMessage('error', err.response?.data?.message || 'Failed to clear throttle');
    }
  };
  const throttleStatus = (r) => (r.locked
    ? `Locked · ~${Math.max(1, Math.ceil((r.retry_after_seconds || 0) / 60))} min left`
    : 'Lapsed');

  // Format date
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  // Get current setting value (with pending changes)
  const getSettingValue = (category, key, defaultValue) => {
    const changeKey = `${category}.${key}`;
    if (settingChanges[changeKey]) {
      return settingChanges[changeKey].value;
    }
    const categorySettings = systemSettings[category] || [];
    const setting = categorySettings.find(s => s.setting_key === key);
    return setting?.setting_value || defaultValue;
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>⚙️ Settings</h1>
        <p>Configure system settings, permissions, and integrations</p>
      </div>

      {/* Message — fixed toast so it's visible regardless of scroll position
          (the in-flow banner at the page top was off-screen when saving from
          the Roles matrix). Solid dark background keeps the themed border/text
          from the settings-message classes readable over any content. */}
      {message.text && (
        <div
          className={`settings-message ${message.type}`}
          style={{
            position: 'fixed',
            top: '80px',
            right: '24px',
            zIndex: 1000,
            background: '#1A1A1A',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
            maxWidth: '360px'
          }}
        >
          {message.type === 'success' ? '✓' : '⚠'} {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="settings-tabs">
        <button 
          className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 Profile
        </button>
        {canManage && (
          <>
            <button 
              className={`settings-tab ${activeTab === 'appearance' ? 'active' : ''}`}
              onClick={() => setActiveTab('appearance')}
            >
              🎨 Appearance
            </button>
            <button 
              className={`settings-tab ${activeTab === 'system' ? 'active' : ''}`}
              onClick={() => setActiveTab('system')}
            >
              🔧 System
            </button>
            <button 
              className={`settings-tab ${activeTab === 'roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('roles')}
            >
              🎭 Roles
            </button>
            <button 
              className={`settings-tab ${activeTab === 'overrides' ? 'active' : ''}`}
              onClick={() => setActiveTab('overrides')}
            >
              🔓 Overrides
            </button>
            <button 
              className={`settings-tab ${activeTab === 'audit' ? 'active' : ''}`}
              onClick={() => setActiveTab('audit')}
            >
              📋 Audit
            </button>
            <button 
              className={`settings-tab ${activeTab === 'sessions' ? 'active' : ''}`}
              onClick={() => setActiveTab('sessions')}
            >
              🖥️ Sessions
            </button>
          </>
        )}
        {canManageDepartments && (
          <button
            className={`settings-tab ${activeTab === 'departments' ? 'active' : ''}`}
            onClick={() => setActiveTab('departments')}
          >
            🏢 Departments
          </button>
        )}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="settings-grid">
          <div className="settings-card">
            <div className="card-header">
              <h3>👤 Profile Information</h3>
            </div>
            <div className="card-body">
              <div className="profile-info">
                <div className="profile-row">
                  <span className="label">Name</span>
                  <span className="value">{user?.name}</span>
                </div>
                <div className="profile-row">
                  <span className="label">Email</span>
                  <span className="value">{user?.email}</span>
                </div>
                <div className="profile-row">
                  <span className="label">Role</span>
                  <span className="value role-badge">{user?.role}</span>
                </div>
                <div className="profile-row">
                  <span className="label">Position</span>
                  <span className="value">{user?.position || 'Not set'}</span>
                </div>
                <div className="profile-row">
                  <span className="label">Department</span>
                  <span className="value">{user?.department || 'Not set'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="settings-card">
            <div className="card-header">
              <h3>🔐 Change Password</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handlePasswordChange}>
                <div className="form-group">
                  <label>Current Password</label>
                  <input 
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={e => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input 
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={e => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    required
                    minLength={12}
                    maxLength={72}
                  />
                  <small className="form-hint">12 to 72 characters; checked against known data breaches.</small>
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input 
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={e => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    required
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>

          <div className="settings-card">
            <div className="card-header">
              <h3>ℹ️ System Information</h3>
            </div>
            <div className="card-body">
              <div className="profile-info">
                <div className="profile-row">
                  <span className="label">API Version</span>
                  <span className="value">{apiHealth?.version || 'Loading...'}</span>
                </div>
                <div className="profile-row">
                  <span className="label">Build</span>
                  <span className="value">{apiHealth?.build || '-'}</span>
                </div>
                <div className="profile-row">
                  <span className="label">API Status</span>
                  <span className={`value status-${apiHealth?.status === 'OK' ? 'ok' : 'error'}`}>
                    {apiHealth?.status || 'Unknown'}
                  </span>
                </div>
                <div className="profile-row">
                  <span className="label">Server Time</span>
                  <span className="value">{apiHealth?.timestamp ? formatDate(apiHealth.timestamp) : '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Appearance Tab - Dashboard Customization */}
      {activeTab === 'appearance' && canManage && (
        <div className="settings-content">
          <div className="settings-actions-bar">
            <div>
              <h2>🎨 Dashboard Appearance</h2>
              <p className="text-muted">Customize the look of your Dashboard</p>
            </div>
            {Object.keys(settingChanges).length > 0 && (
              <button className="btn-primary" onClick={saveSystemSettings} disabled={loading}>
                💾 Save Changes ({Object.keys(settingChanges).length})
              </button>
            )}
          </div>

          <div className="settings-card">
            <div className="card-header">
              <h3>🖼️ Hero Background Image</h3>
            </div>
            <div className="card-body">
              {/* Upload Button */}
              <div className="form-group">
                <label>Upload Image</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input 
                    type="file"
                    ref={heroFileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleHeroImageUpload}
                    style={{ display: 'none' }}
                    id="hero-upload"
                  />
                  <button 
                    type="button"
                    className="btn-primary"
                    onClick={() => heroFileInputRef.current?.click()}
                    disabled={uploadingHero}
                    style={{ minWidth: '160px' }}
                  >
                    {uploadingHero ? '⏳ Uploading...' : '📤 Upload Image'}
                  </button>
                  <span style={{ color: '#A0A0A0', fontSize: '14px' }}>JPEG, PNG, GIF, WebP (max 10MB)</span>
                </div>
              </div>

              <div style={{ margin: '20px 0', borderTop: '1px solid #2A2A2A', paddingTop: '20px' }}>
                <span style={{ color: '#A0A0A0', fontSize: '14px' }}>— OR enter a URL directly —</span>
              </div>

              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="text"
                  value={getSettingValue('appearance', 'hero_image_url', 'https://images.unsplash.com/photo-1557223562-6c77ef16210f?w=1600&h=400&fit=crop&crop=center&q=80')}
                  onChange={e => handleSettingChange('appearance', 'hero_image_url', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
                <small>Enter a URL for the Dashboard hero background. Use high-resolution images (1600x400 or larger).</small>
              </div>

              <div style={{ marginTop: '20px' }}>
                <label>Preview</label>
                <div style={{
                  height: '140px',
                  borderRadius: '12px',
                  marginTop: '8px',
                  backgroundImage: `linear-gradient(135deg, rgba(212, 169, 78, 0.88) 0%, rgba(184, 150, 62, 0.85) 50%, rgba(160, 130, 50, 0.88) 100%), url('${getSettingValue('appearance', 'hero_image_url', 'https://images.unsplash.com/photo-1557223562-6c77ef16210f?w=1600&h=400&fit=crop&crop=center&q=80')}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center 30%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}>
                  Welcome back!
                </div>
              </div>

              <div className="camera-info-box" style={{ marginTop: '20px' }}>
                <h4>💡 Image Tips</h4>
                <ul>
                  <li><strong>Upload:</strong> Click "Upload Image" to upload directly from your computer</li>
                  <li><strong>Unsplash:</strong> Free photos at unsplash.com/s/photos/school-bus</li>
                  <li><strong>Size:</strong> 1600x400 pixels or larger recommended</li>
                  <li><strong>Format:</strong> JPG or PNG work best</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Support Page Hero Image */}
          <div className="settings-card">
            <div className="card-header">
              <h3>🎫 Support Page Hero Image</h3>
            </div>
            <div className="card-body">
              {/* Upload Button */}
              <div className="form-group">
                <label>Upload Image</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input
                    type="file"
                    ref={supportHeroFileInputRef}
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleSupportHeroImageUpload}
                    style={{ display: 'none' }}
                    id="support-hero-upload"
                  />
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => supportHeroFileInputRef.current?.click()}
                    disabled={uploadingSupportHero}
                    style={{ minWidth: '160px' }}
                  >
                    {uploadingSupportHero ? '⏳ Uploading...' : '📤 Upload Image'}
                  </button>
                  <span style={{ color: '#A0A0A0', fontSize: '14px' }}>JPEG, PNG, GIF, WebP (max 10MB)</span>
                </div>
              </div>

              <div style={{ margin: '20px 0', borderTop: '1px solid #2A2A2A', paddingTop: '20px' }}>
                <span style={{ color: '#A0A0A0', fontSize: '14px' }}>— OR enter a URL directly —</span>
              </div>

              <div className="form-group">
                <label>Image URL</label>
                <input
                  type="text"
                  value={getSettingValue('appearance', 'support_hero_image_url', 'https://images.unsplash.com/photo-1560264280-88b68371db39?w=1600&h=400&fit=crop&crop=center&q=80')}
                  onChange={e => handleSettingChange('appearance', 'support_hero_image_url', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                />
                <small>Enter a URL for the Support page hero background. Use high-resolution images (1600x400 or larger).</small>
              </div>

              <div style={{ marginTop: '20px' }}>
                <label>Preview</label>
                <div style={{
                  height: '140px',
                  borderRadius: '12px',
                  marginTop: '8px',
                  backgroundImage: `linear-gradient(135deg, rgba(212, 169, 78, 0.5) 0%, rgba(184, 150, 62, 0.45) 50%, rgba(160, 130, 50, 0.5) 100%), url('${getSettingValue('appearance', 'support_hero_image_url', 'https://images.unsplash.com/photo-1560264280-88b68371db39?w=1600&h=400&fit=crop&crop=center&q=80')}')`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  textShadow: '0 2px 4px rgba(0,0,0,0.4)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}>
                  IT Support
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* System Settings Tab */}
      {activeTab === 'system' && canManage && (
        <div className="settings-content">
          <div className="settings-actions-bar">
            <h2>System Settings</h2>
            {Object.keys(settingChanges).length > 0 && (
              <button className="btn-primary" onClick={saveSystemSettings} disabled={loading}>
                💾 Save Changes ({Object.keys(settingChanges).length})
              </button>
            )}
          </div>

          <div className="settings-card">
            <div className="card-header">
              <h3>🔒 Security</h3>
            </div>
            <div className="card-body">
              <div className="settings-form-grid">
                <div className="form-group">
                  <label>Max Login Attempts</label>
                  <input 
                    type="number"
                    value={getSettingValue('security', 'max_login_attempts', '5')}
                    onChange={e => handleSettingChange('security', 'max_login_attempts', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Lockout Duration (minutes)</label>
                  <input 
                    type="number"
                    value={getSettingValue('security', 'lockout_duration_minutes', '15')}
                    onChange={e => handleSettingChange('security', 'lockout_duration_minutes', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Locked accounts (throttle-UI slice): every login_throttle row,
              locked or lapsed; Unlock = the P1-C admin clear. Sessions-card pattern. */}
          <div className="settings-card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>🔐 Locked accounts</h3>
              <button className="btn-secondary" onClick={fetchThrottle}>
                🔄 Refresh
              </button>
            </div>
            <div className="card-body">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Identifier</th>
                    <th>Failures</th>
                    <th>Last failure</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {throttle.map(r => (
                    <tr key={r.identifier}>
                      <td>
                        <span className="monospace">{r.identifier}</span>
                        {r.user_name && <div className="text-small text-muted">{r.user_name}</div>}
                        {r.kind === 'password_change' && <div className="text-small text-muted">password-change lock</div>}
                      </td>
                      <td>{r.failures}</td>
                      <td>{formatDate(r.last_failure_at)}</td>
                      <td>{throttleStatus(r)}</td>
                      <td>
                        <button className="btn-danger-small" onClick={() => unlockIdentifier(r.identifier)}>
                          Unlock
                        </button>
                      </td>
                    </tr>
                  ))}
                  {throttle.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No throttled identifiers.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="settings-card">
            <div className="card-header">
              <h3>🔔 Notifications</h3>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="toggle-label">
                  <input 
                    type="checkbox"
                    checked={getSettingValue('notifications', 'accident_alert_enabled', 'true') === 'true'}
                    onChange={e => handleSettingChange('notifications', 'accident_alert_enabled', e.target.checked ? 'true' : 'false')}
                  />
                  <span className="toggle-switch"></span>
                  Send alerts for new accident reports
                </label>
              </div>
              <div className="form-group">
                <label>Certification Expiry Alert (days before)</label>
                <input 
                  type="number"
                  style={{ maxWidth: '200px' }}
                  value={getSettingValue('notifications', 'certification_expiry_days', '30')}
                  onChange={e => handleSettingChange('notifications', 'certification_expiry_days', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* NR1 — Notification routing (docs/NOTIF_ROUTING_PROGRAM.md §3.8).
              Its own Save: the document is validated server-side and reloaded
              after the write, so the card always shows what is stored. */}
          <div className="settings-card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>📬 Notification routing</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {routingDirty && <span className="text-small warning-text">Unsaved changes</span>}
                <button className="btn-secondary" onClick={fetchRouting} disabled={loading}>🔄 Reload</button>
                <button className="btn-primary" onClick={saveRouting} disabled={loading || !routingDirty}>💾 Save routing</button>
              </div>
            </div>
            <div className="card-body">
              <p className="text-small text-muted">
                Who is told when an event happens — by role and department, never by named user.
                Department names must match exactly (case does not matter). Digest delivery is coming later.
              </p>
              {routingErrors.length > 0 && (
                <ul className="warning-text text-small">
                  {routingErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
              {!routing && (
                <div className="empty-state">No routing document is stored yet. It is seeded at the next server start.</div>
              )}
              {routing && Object.keys(routing).map(type => {
                const route = routing[type];
                const deptOptions = [...new Set([...(routingDepartments || []), ...(route.departments || [])])];
                // TO1b (R-TO6): a targeted type is addressed by the event
                // itself (the requester's manager, the requester). Roles and
                // departments MUST stay empty — the server rejects otherwise —
                // so they are shown disabled rather than hidden, to make the
                // difference between the two kinds visible.
                const isTargeted = route.audience === 'targeted';
                return (
                  <div key={type} style={{ borderTop: '1px solid #2A2A2A', paddingTop: '16px', marginTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      <strong className="monospace">{type}</strong>
                      <label className="toggle-label" style={{ marginBottom: 0 }}>
                        <input type="checkbox" checked={route.enabled === true}
                          onChange={e => updateRoute(type, { enabled: e.target.checked })} />
                        <span className="toggle-switch"></span>
                        Enabled
                      </label>
                      <select value={route.immediacy || 'immediate'} style={{ width: 'auto' }}
                        onChange={e => updateRoute(type, { immediacy: e.target.value })}>
                        <option value="immediate">Immediate</option>
                        <option value="digest" disabled>Digest (coming soon)</option>
                        <option value="off">Off</option>
                      </select>
                      {isTargeted && (
                        <span className="text-small text-muted" style={{ fontStyle: 'italic' }}>
                          targeted by the event — recipients come from the request itself
                        </span>
                      )}
                    </div>
                    <div className="settings-form-grid">
                      <div className="form-group">
                        <label>Roles</label>
                        {routingRoles.map(r => (
                          <label key={r.name} className="toggle-label" style={{ fontWeight: 400, marginBottom: '6px', opacity: isTargeted ? 0.45 : 1 }}>
                            <input type="checkbox" disabled={isTargeted} checked={(route.roles || []).includes(r.name)}
                              onChange={() => toggleRouteListItem(type, 'roles', r.name)} />
                            <span className="toggle-switch"></span>
                            {r.display_name || r.name}
                          </label>
                        ))}
                        {routingRoles.length === 0 && <small>No roles loaded.</small>}
                      </div>
                      <div className="form-group">
                        <label>Departments</label>
                        {deptOptions.map(d => (
                          <label key={d} className="toggle-label" style={{ fontWeight: 400, marginBottom: '6px', opacity: isTargeted ? 0.45 : 1 }}>
                            <input type="checkbox" disabled={isTargeted} checked={(route.departments || []).includes(d)}
                              onChange={() => toggleRouteListItem(type, 'departments', d)} />
                            <span className="toggle-switch"></span>
                            {d}
                          </label>
                        ))}
                        {!isTargeted && (
                          <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                            <input type="text" placeholder="Add a department name"
                              value={routingDeptDraft[type] || ''}
                              onChange={e => setRoutingDeptDraft(prev => ({ ...prev, [type]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRouteDepartment(type); } }} />
                            <button type="button" className="btn-secondary" onClick={() => addRouteDepartment(type)}>Add</button>
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Channels</label>
                        <label className="toggle-label" style={{ fontWeight: 400, marginBottom: '6px' }}>
                          <input type="checkbox" checked={route.channels?.bell !== false}
                            onChange={e => updateRoute(type, { channels: { ...(route.channels || {}), bell: e.target.checked } })} />
                          <span className="toggle-switch"></span>
                          Bell (in-portal + push)
                        </label>
                        <label className="toggle-label" style={{ fontWeight: 400, marginBottom: '6px' }}>
                          <input type="checkbox" checked={route.channels?.email !== false}
                            onChange={e => updateRoute(type, { channels: { ...(route.channels || {}), email: e.target.checked } })} />
                          <span className="toggle-switch"></span>
                          Email
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="settings-card">
            <div className="card-header">
              <h3>🏢 Company</h3>
            </div>
            <div className="card-body">
              <div className="settings-form-grid">
                <div className="form-group">
                  <label>Company Name</label>
                  <input 
                    type="text"
                    value={getSettingValue('company', 'company_name', 'GVC School Bus')}
                    onChange={e => handleSettingChange('company', 'company_name', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Company Phone</label>
                  <input 
                    type="text"
                    value={getSettingValue('company', 'company_phone', '')}
                    onChange={e => handleSettingChange('company', 'company_phone', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Company Email</label>
                  <input 
                    type="email"
                    value={getSettingValue('company', 'company_email', '')}
                    onChange={e => handleSettingChange('company', 'company_email', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="settings-card">
            <div className="card-header">
              <h3>🔗 API Integrations</h3>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="toggle-label">
                  <input 
                    type="checkbox"
                    checked={getSettingValue('api', 'bushive_enabled', 'false') === 'true'}
                    onChange={e => handleSettingChange('api', 'bushive_enabled', e.target.checked ? 'true' : 'false')}
                  />
                  <span className="toggle-switch"></span>
                  Enable BusHive Integration
                  <small>Compliance tracking sync</small>
                </label>
              </div>
              <div className="form-group">
                <label className="toggle-label">
                  <input 
                    type="checkbox"
                    checked={getSettingValue('api', 'payserv_enabled', 'false') === 'true'}
                    onChange={e => handleSettingChange('api', 'payserv_enabled', e.target.checked ? 'true' : 'false')}
                  />
                  <span className="toggle-switch"></span>
                  Enable PayServ Integration
                  <small>Payroll/attendance sync</small>
                </label>
              </div>
              <div className="form-group">
                <label className="toggle-label">
                  <input 
                    type="checkbox"
                    checked={getSettingValue('api', 'syncup_fleet_enabled', 'false') === 'true'}
                    onChange={e => handleSettingChange('api', 'syncup_fleet_enabled', e.target.checked ? 'true' : 'false')}
                  />
                  <span className="toggle-switch"></span>
                  Enable T-Mobile SyncUp Fleet
                  <small>GPS fleet tracking (Geotab)</small>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && canManage && (
        <div className="settings-content">
          <h2>Role Permissions</h2>
          <p className="text-muted">Configure what each role can access. Changes apply to all users with that role.</p>

          <div className="roles-layout">
            <div className="settings-card roles-sidebar">
              <div className="card-header">
                <h3>Roles</h3>
              </div>
              <div className="card-body">
                {roles.map(role => (
                  <div 
                    key={role.id}
                    className={`role-item ${selectedRole === role.name ? 'active' : ''}`}
                    onClick={() => setSelectedRole(role.name)}
                  >
                    <strong>{role.display_name || role.name}</strong>
                    <span className="role-count">{role.user_count} user{role.user_count !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            </div>

            {selectedRole && (
              <div className="settings-card roles-matrix">
                <div className="card-header">
                  <h3>Permissions for: {selectedRole}</h3>
                  <button className="btn-primary" onClick={saveRolePermissions} disabled={loading}>
                    💾 Save Permissions
                  </button>
                </div>
                <div className="card-body">
                  {Object.keys(moduleCatalog).length === 0 ? (
                    <div className="loading">Loading modules...</div>
                  ) : (
                  <div className="permissions-table-wrapper">
                    <table className="permissions-table">
                      <thead>
                        <tr>
                          <th>Module</th>
                          {Object.entries(ACTIONS).map(([action, label]) => (
                            <th key={action}>{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(moduleCatalog).map(([module, actions]) => (
                          <tr key={module}>
                            <td><strong>{moduleLabel(module)}</strong></td>
                            {Object.keys(ACTIONS).map(action => (
                              <td key={action} className="text-center">
                                {actions.includes(action) ? (
                                  <input
                                    type="checkbox"
                                    checked={(rolePermissions[module] || []).includes(action)}
                                    onChange={() => toggleRolePermission(module, action)}
                                    disabled={selectedRole === 'admin'}
                                  />
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}
                  {selectedRole === 'admin' && (
                    <p className="warning-text">⚠️ Admin role has full access and cannot be modified.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User Overrides Tab */}
      {activeTab === 'overrides' && canManage && (
        <div className="settings-content">
          <div className="settings-actions-bar">
            <div>
              <h2>User Permission Overrides</h2>
              <p className="text-muted">Grant or revoke specific permissions for individual users.</p>
            </div>
            <button className="btn-primary" onClick={() => setShowOverrideModal(true)}>
              + Add Override
            </button>
          </div>

          {userOverrides.length === 0 ? (
            <div className="settings-card">
              <div className="empty-state">
                <span className="empty-icon">🔓</span>
                <p>No user permission overrides configured.</p>
              </div>
            </div>
          ) : (
            <div className="settings-card">
              <div className="card-body">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Module</th>
                      <th>Action</th>
                      <th>Status</th>
                      <th>Reason</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userOverrides.map(override => (
                      <tr key={override.id}>
                        <td>
                          <strong>{override.user_name}</strong>
                          <div className="text-small text-muted">{override.user_email}</div>
                        </td>
                        <td>{override.user_role}</td>
                        <td>{moduleLabel(override.module)}</td>
                        <td>{override.action}</td>
                        <td>
                          <span className={`status-badge ${override.granted ? 'granted' : 'revoked'}`}>
                            {override.granted ? '✓ Granted' : '✗ Revoked'}
                          </span>
                        </td>
                        <td>{override.reason || '-'}</td>
                        <td>
                          <button className="btn-danger-small" onClick={() => deleteOverride(override.id)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Audit Log Tab */}
      {activeTab === 'audit' && canManage && (
        <div className="settings-content">
          <h2>Audit Log</h2>
          
          <div className="settings-card">
            <div className="card-body">
              <div className="filter-row">
                <div className="form-group">
                  <label>User</label>
                  <select 
                    value={auditFilters.user_id}
                    onChange={e => setAuditFilters({...auditFilters, user_id: e.target.value})}
                  >
                    <option value="">All Users</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Action</label>
                  <input 
                    type="text"
                    placeholder="e.g., create, delete"
                    value={auditFilters.action}
                    onChange={e => setAuditFilters({...auditFilters, action: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Entity Type</label>
                  <select 
                    value={auditFilters.entity_type}
                    onChange={e => setAuditFilters({...auditFilters, entity_type: e.target.value})}
                  >
                    <option value="">All Types</option>
                    <option value="employees">Employees</option>
                    <option value="accidents">Accidents</option>
                    <option value="hr_applicants">Applicants</option>
                    <option value="settings">Settings</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <input 
                    type="date"
                    value={auditFilters.start_date}
                    onChange={e => setAuditFilters({...auditFilters, start_date: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input 
                    type="date"
                    value={auditFilters.end_date}
                    onChange={e => setAuditFilters({...auditFilters, end_date: e.target.value})}
                  />
                </div>
                <div className="filter-actions">
                  <button className="btn-secondary" onClick={() => {
                    setAuditFilters({ user_id: '', action: '', entity_type: '', start_date: '', end_date: '' });
                    setAuditPagination(p => ({ ...p, offset: 0 }));
                  }}>
                    Clear
                  </button>
                  <button className="btn-primary" onClick={() => {
                    setAuditPagination(p => ({ ...p, offset: 0 }));
                    fetchAuditLogs();
                  }}>
                    🔍 Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="settings-card">
            <div className="card-body">
              <div className="table-info">Showing {auditLogs.length} of {auditPagination.total} records</div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Entity</th>
                    <th>IP Address</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id}>
                      <td className="nowrap">{formatDate(log.created_at)}</td>
                      <td>{log.user_name || 'System'}</td>
                      <td>
                        <span className={`action-badge ${log.action?.includes('delete') ? 'delete' : log.action?.includes('create') ? 'create' : 'edit'}`}>
                          {log.action}
                        </span>
                      </td>
                      <td>{log.entity_type}</td>
                      <td className="monospace">{log.ip_address || '-'}</td>
                      <td className="truncate">
                        {log.details ? JSON.stringify(log.details).substring(0, 50) + '...' : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {auditPagination.total > auditPagination.limit && (
                <div className="pagination">
                  <button 
                    className="btn-secondary"
                    disabled={auditPagination.offset === 0}
                    onClick={() => setAuditPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
                  >
                    ← Previous
                  </button>
                  <span className="page-info">
                    Page {Math.floor(auditPagination.offset / auditPagination.limit) + 1} of {Math.ceil(auditPagination.total / auditPagination.limit)}
                  </span>
                  <button 
                    className="btn-secondary"
                    disabled={auditPagination.offset + auditPagination.limit >= auditPagination.total}
                    onClick={() => setAuditPagination(p => ({ ...p, offset: p.offset + p.limit }))}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Sessions Tab */}
      {/* Departments Tab (TO1a / R-TO4) */}
      {activeTab === 'departments' && canManageDepartments && (
        <div className="settings-card">
          <div className="card-header">
            <h3>Departments</h3>
          </div>
          <div className="card-body">
            <p style={{ marginBottom: '16px', opacity: 0.8 }}>
              Departments are the org unit for time off. Each has exactly one manager,
              who approves their people's requests before the General Manager.
              Names are matched to an employee's department field exactly, ignoring
              case and surrounding spaces.
            </p>

            <div className="permissions-table-wrapper">
              <table className="permissions-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Colour</th>
                    <th>Manager</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.length === 0 && (
                    <tr><td colSpan="5">No departments yet.</td></tr>
                  )}
                  {departments.map(d => (
                    <tr key={d.id} style={d.is_active ? undefined : { opacity: 0.55 }}>
                      {editingDeptId === d.id ? (
                        <>
                          <td>
                            <input
                              type="text"
                              value={editingDept.name}
                              onChange={e => setEditingDept({ ...editingDept, name: e.target.value })}
                            />
                          </td>
                          <td>
                            <input
                              type="color"
                              value={editingDept.color}
                              onChange={e => setEditingDept({ ...editingDept, color: e.target.value })}
                            />
                          </td>
                          <td>
                            <select
                              value={editingDept.manager_user_id || ''}
                              onChange={e => setEditingDept({ ...editingDept, manager_user_id: e.target.value })}
                            >
                              <option value="">— no manager —</option>
                              {users.filter(u => u.is_active !== false).map(u => (
                                <option key={u.id} value={u.id}>{u.name}</option>
                              ))}
                            </select>
                          </td>
                          <td>{d.is_active ? 'Active' : 'Inactive'}</td>
                          <td>
                            <button className="btn-primary" disabled={loading} onClick={() => saveDepartment(d.id)}>Save</button>
                            <button className="btn-secondary" onClick={() => setEditingDeptId(null)}>Cancel</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td><strong>{d.name}</strong></td>
                          <td>
                            <span style={{
                              display: 'inline-block', width: '18px', height: '18px',
                              borderRadius: '4px', background: d.color,
                              verticalAlign: 'middle', marginRight: '8px'
                            }} />
                            {d.color}
                          </td>
                          <td>{d.manager_name || <em>— unassigned —</em>}</td>
                          <td>{d.is_active ? 'Active' : 'Inactive'}</td>
                          <td>
                            <button
                              className="btn-secondary"
                              onClick={() => {
                                setEditingDeptId(d.id);
                                setEditingDept({
                                  name: d.name,
                                  color: d.color,
                                  manager_user_id: d.manager_user_id || ''
                                });
                              }}
                            >Edit</button>
                            <button
                              className="btn-secondary"
                              disabled={loading}
                              onClick={() => toggleDepartmentActive(d)}
                            >{d.is_active ? 'Deactivate' : 'Activate'}</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card-header" style={{ marginTop: '24px' }}>
              <h3>Add a department</h3>
            </div>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={deptDraft.name}
                placeholder="e.g. Dispatch"
                onChange={e => setDeptDraft({ ...deptDraft, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Colour</label>
              <input
                type="color"
                value={deptDraft.color}
                onChange={e => setDeptDraft({ ...deptDraft, color: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Manager</label>
              <select
                value={deptDraft.manager_user_id}
                onChange={e => setDeptDraft({ ...deptDraft, manager_user_id: e.target.value })}
              >
                <option value="">— no manager —</option>
                {users.filter(u => u.is_active !== false).map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <button className="btn-primary" disabled={loading} onClick={createDepartment}>
              ➕ Add department
            </button>

            {/* Blackout dates (TO1c / R-TO7, ruling 11). Same tab, same gate:
                this is the GM's time-off configuration surface. A blackout is
                a WARNING — it shows on My Time Off before submit, on the
                approvers' rows, and as a shaded band on the Who's Out
                calendar. It never refuses a request. */}
            <div className="card-header" style={{ marginTop: '32px' }}>
              <h3>Blackout dates</h3>
            </div>
            <p style={{ marginBottom: '16px', opacity: 0.8 }}>
              Periods when time off is discouraged. Requests overlapping a blackout are
              still accepted — the requester and both approvers simply see the warning.
              Blackouts may overlap each other. Deactivate rather than delete, so a
              blackout that has already warned somebody stays on the record.
            </p>

            <div className="permissions-table-wrapper">
              <table className="permissions-table">
                <thead>
                  <tr>
                    <th>From</th>
                    <th>To</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blackouts.length === 0 && (
                    <tr><td colSpan="5">No blackout dates set.</td></tr>
                  )}
                  {blackouts.map(b => (
                    <tr key={b.id} style={b.is_active ? undefined : { opacity: 0.55 }}>
                      <td>{formatDateOnly(b.start_date)}</td>
                      <td>{formatDateOnly(b.end_date)}</td>
                      <td>{b.reason}</td>
                      <td>{b.is_active ? 'Active' : 'Inactive'}</td>
                      <td>
                        <button
                          className="btn-secondary"
                          disabled={loading}
                          onClick={() => toggleBlackoutActive(b)}
                        >{b.is_active ? 'Deactivate' : 'Reactivate'}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="card-header" style={{ marginTop: '24px' }}>
              <h3>Add a blackout</h3>
            </div>
            <div className="form-group">
              <label>First day</label>
              <input
                type="date"
                value={blackoutDraft.start_date}
                onChange={e => setBlackoutDraft({ ...blackoutDraft, start_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Last day</label>
              <input
                type="date"
                value={blackoutDraft.end_date}
                onChange={e => setBlackoutDraft({ ...blackoutDraft, end_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Reason</label>
              <input
                type="text"
                value={blackoutDraft.reason}
                placeholder="e.g. First week of school"
                onChange={e => setBlackoutDraft({ ...blackoutDraft, reason: e.target.value })}
              />
            </div>
            <button className="btn-primary" disabled={loading} onClick={createBlackout}>
              ➕ Add blackout
            </button>
          </div>
        </div>
      )}

      {activeTab === 'sessions' && canManage && (
        <div className="settings-content">
          <div className="settings-actions-bar">
            <h2>Active Sessions</h2>
            <button className="btn-secondary" onClick={fetchSessions}>
              🔄 Refresh
            </button>
          </div>

          <div className="settings-card">
            <div className="card-body">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Device</th>
                    <th>IP Address</th>
                    <th>Last Activity</th>
                    <th>Started</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map(session => (
                    <tr key={session.id}>
                      <td>
                        <strong>{session.user_name}</strong>
                        <div className="text-small text-muted">{session.user_email}</div>
                      </td>
                      <td>{session.user_role}</td>
                      <td>{session.device_label || '-'}</td>
                      <td className="monospace">{session.ip_address || '-'}</td>
                      <td>{formatDate(session.last_activity)}</td>
                      <td>{formatDate(session.created_at)}</td>
                      <td>
                        {session.id === sessionId ? (
                          <span className="current-badge">Current</span>
                        ) : (
                          <button className="btn-danger-small" onClick={() => terminateSession(session.id)}>
                            Terminate
                          </button>
                        )}
                        {' '}
                        <button className="btn-danger-small" title="Sign this user out of every device" onClick={() => signOutEverywhere(session.user_id, session.user_name)}>
                          All devices
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">
                        No active sessions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add Override Modal */}
      {showOverrideModal && (
        <div className="modal-overlay" onClick={() => setShowOverrideModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Permission Override</h2>
              <button className="modal-close" onClick={() => setShowOverrideModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>User *</label>
                <select 
                  value={newOverride.user_id}
                  onChange={e => setNewOverride({...newOverride, user_id: e.target.value})}
                >
                  <option value="">Select User</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Module *</label>
                <select 
                  value={newOverride.module}
                  onChange={e => setNewOverride({...newOverride, module: e.target.value, action: ''})}
                >
                  <option value="">Select Module</option>
                  {Object.keys(moduleCatalog).map(key => (
                    <option key={key} value={key}>{moduleLabel(key)}</option>
                  ))}
                </select>
              </div>
              {newOverride.module && (
                <div className="form-group">
                  <label>Action *</label>
                  <select 
                    value={newOverride.action}
                    onChange={e => setNewOverride({...newOverride, action: e.target.value})}
                  >
                    <option value="">Select Action</option>
                    {(moduleCatalog[newOverride.module] || []).map(action => (
                      <option key={action} value={action}>{action}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Permission</label>
                <select 
                  value={newOverride.granted ? 'grant' : 'revoke'}
                  onChange={e => setNewOverride({...newOverride, granted: e.target.value === 'grant'})}
                >
                  <option value="grant">✓ Grant (add permission)</option>
                  <option value="revoke">✗ Revoke (remove permission)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Reason (optional)</label>
                <textarea 
                  value={newOverride.reason}
                  onChange={e => setNewOverride({...newOverride, reason: e.target.value})}
                  placeholder="Why is this override needed?"
                  rows={2}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowOverrideModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={addUserOverride}
                disabled={!newOverride.user_id || !newOverride.module || !newOverride.action || loading}
              >
                {loading ? 'Adding...' : 'Add Override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
